import { useState } from "react";
import api from "src/features/auth/axiosConfig";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";
import { Order, OrderedProduct } from "src/shared/services/interfacev2";
import { postCustomer, postOrder, putOrder } from "src/shared/services/servicesv2";

interface UseOrderOperationsProps {
  selectedProducts: OrderedProduct[];
  setSelectedProducts: React.Dispatch<React.SetStateAction<OrderedProduct[]>>;
  firstName: string;
  setFirstName: (name: string) => void;
  middleName: string;
  setMiddleName: (name: string) => void;
  lastName: string;
  setLastName : (name: string) => void;
  phoneNumber: string;
  setPhoneNumber: (phone: string) => void;
  tableNumber: string;
  setTableNumber: (table: string) => void;
  discount: number;
  setDiscount: (discount: number) => void;
  oldProducts: Order | undefined;
  setOldProducts: (order: Order | undefined) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  guess: string;
  setGuess: (guess: string) => void;
  calculateSubtotal: () => number;
  calculateTotal: (subtotal: number) => number;
  isDelivery: boolean;
  headerRef: any;
  setCustomerAdd: (add: boolean) => void;
  setSelectedCategory: (category: string | null) => void;
  setSelectedCategoryradio: (category: string) => void;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  fetchCustomerDetails: () => Promise<void>;
  deliveryFee: number;
  setDeliveryFee: (fee: number) => void;
  parcelFee: number;
  setParcelFee: (fee: number) => void;
}

export const useOrderOperations = ({
  selectedProducts,
  setSelectedProducts,
  firstName, 
  setFirstName,
  middleName,
  setMiddleName,
  lastName,
  setLastName,
  phoneNumber,
  setPhoneNumber,
  tableNumber,
  setTableNumber,
  discount,
  setDiscount,
  oldProducts,
  setOldProducts,
  isEditing,
  setIsEditing,
  guess,
  setGuess,
  calculateSubtotal,
  calculateTotal,
  isDelivery,
  headerRef,
  setCustomerAdd,
  setSelectedCategory,
  setSelectedCategoryradio,
  setSearchQuery,
  setCurrentPage,
  fetchCustomerDetails,
  deliveryFee,
  setDeliveryFee,
  parcelFee,
  setParcelFee,
}: UseOrderOperationsProps) => {
  const [open, setOpen] = useState(false);
  const [newOrderId, setNewOrderId] = useState<string>("");

  const resetFormFields = () => {
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setPhoneNumber("");
    setTableNumber("");
    setSelectedProducts([]);
    setDiscount(0);
    setDeliveryFee(0);
    setOldProducts(undefined);
    setIsEditing(false);
    setGuess("");
    setSelectedCategory(null);
    setSelectedCategoryradio("none");
    setSearchQuery("");
    setCurrentPage(0);
    window.history.replaceState({}, "", window.location.pathname);
  };

  /**
   * ✅ OrderedProduct → API payload
   */
  const transformProductsForAPI = (products: OrderedProduct[]) =>
    products.map((item) => ({
      productId: item.productId,
      productName: item.name,
      arabicName: item.arabicName,
      notes: item.notes || "",
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      itemTotal: item.lineTotal,
      type: item.type,
    }));

  const handleCancelOrder = async () => {
    if (!oldProducts) {
      notifyWarning("No order to cancel!");
      return;
    }

    try {
      await api.put(`/app/pos/v2/order/${oldProducts.orderId}`, {
        orderStatus: "Cancelled",
      });

      notifySuccess("Order cancelled successfully!");
      resetFormFields();
    } catch (error) {
      console.error(error);
      notifyError("Failed to cancel the order.");
    }
  };

  const handleUpdateOrder = async (isOrderModified: boolean) => {
    if (isEditing && !isOrderModified) {
      notifyWarning("No changes detected.");
      return;
    }

    if (!selectedProducts.length) {
      notifyWarning("Please select at least one product!");
      return;
    }

    const subtotal = calculateSubtotal();
    const total = subtotal - discount + (isDelivery ? Number(deliveryFee) || 0 : 0) + (Number(parcelFee) || 0);
    const now = new Date();
    const orderTypeForUpdate = isDelivery ? "Delivery" : "Table";
    const payload = {
      guest: Number(guess ?? 0),
      tableNumber: Number(tableNumber ?? 0),
      orderStatus: "pending",
      orderAmount: subtotal,
      discount: Number(discount),
      customerId: oldProducts?.orders?.[0]?.customerId ?? null,
      finalAmount: total,
      products: transformProductsForAPI(selectedProducts),
      deliveryFee: isDelivery ? Number(deliveryFee) || 0 : 0,
      OrderType: orderTypeForUpdate,
      orderDate: now.toISOString().split("T")[0],
      orderTime: now.toTimeString().split(" ")[0],
      outstandingBalance: total,
      remainingAmount: total,
      tip: oldProducts?.orders?.[0]?.tip || 0,
      parcelFee: Number(parcelFee) || 0,
    };
    // Add delivery fee only for delivery orders
    if (isDelivery) {
      payload.deliveryFee = deliveryFee;
    }

    if (!oldProducts) {
      setOpen(true);
      return;
    }

    try {
      await putOrder(oldProducts.orderId, payload);
      // api.patch(`/app/pos/v2/order/${oldProducts.orderId}`, payload);
      resetFormFields();
      await headerRef.current?.refreshOrders();
      notifySuccess("Order updated successfully!");
    } catch (err) {
      console.error(err);
      notifyError("Failed to update order.");
    }
  };

  const sendorder = async (customerId: string) => {
    const now = new Date();
    const orderId = `${now.getMonth() + 1}${now.getDate()}${now.getHours()}${now.getMinutes()}${now.getSeconds()}`;
    setNewOrderId(orderId);

    const subtotal = calculateSubtotal();
    const finalTotal = subtotal - discount + (isDelivery ? Number(deliveryFee) || 0 : 0) + (Number(parcelFee) || 0);

    const orderDetails = {
      customerId,
      orderStatus: "pending",
      guest: Number(guess),
      tableNumber: Number(tableNumber),
      orderAmount: Number(subtotal),
      discount: Number(discount),
      deliveryFee: Number(deliveryFee),
      finalAmount: Number(finalTotal),
      products: transformProductsForAPI(selectedProducts),
      orderDate: now.toISOString().split("T")[0],
      orderTime: now.toTimeString().split(" ")[0],
      orderType: isDelivery ? "Delivery" : "Table",
      outstandingBalance: 0,
      cancelOrderAmount: 0,
      remainingAmount: finalTotal,
      tip: 0,
      parcelFee: Number(parcelFee),
      // payments: { paymentMethod: "Cash", receivedAmount: 0 },
      // paymentHistory: [{ paymentMethod: "Cash", receivedAmount: 0 }],
    };

    try {
      await postOrder(orderDetails);
      // api.post(`/app/pos/v2/order`, orderDetails);
      notifySuccess("Order created successfully!");
      resetFormFields();
      return true;
    } catch (err) {
      console.error(err);
      notifyError("Failed to create order.");
      return false;
    }
  };

  const handleSubmit = async (customerId?: string) => {
    if (!customerId) {
      if (!firstName || !phoneNumber) {
        notifyError("Customer Name and Phone Number are required.");
        return;
      }

      try {
        const res = await postCustomer({
          firstName: firstName,
          middleName: middleName || "", // Optional middle name
          lastName: lastName,
          phones: phoneNumber ? [phoneNumber] : [],
        });

        await fetchCustomerDetails();
        await sendorder(res.data._id);
        setCustomerAdd(false);
        // Reset form fields after successful submission
        setFirstName("");
        setMiddleName("");
        setLastName("");
        setPhoneNumber("");
      } catch (err: any) {
        notifyError(err.response?.data?.message || "Customer creation failed.");
      }
      return;
    }

    await sendorder(customerId);
    setCustomerAdd(false);
  };

  return {
    open,
    setOpen,
    newOrderId,
    handleCancelOrder,
    handleUpdateOrder,
    handleSubmit,
    sendorder,
    resetFormFields,
  };
};
